// Copyright (c) 2026 Finerty Data Ltd. All rights reserved.
//
// This file is part of proprietary software owned by Finerty Data Ltd.
// It is confidential and may not be copied, distributed, modified, or used,
// in whole or in part, without the prior written consent of Finerty Data Ltd.
//
// Author: Ahmed Hosny

/**
 * Typed wrapper around /api/intelligence/admin/** — proxied through the
 * Admin BFF (token relay attaches the JWT). Used by IntelligenceConfigPage.
 *
 * See docs/specs/intelligence-service-admin-config-prd.md §10.
 */

const API_BASE = '/api/intelligence/admin';

export interface ProviderDTO {
  id: string;
  display: string;
  platformOnly: boolean;
}

export interface ModelDTO {
  id: string;
  display: string;
}

export interface VoiceProfile {
  enabled?: boolean;
  locale?: string;
  gender?: 'female' | 'male' | 'any';
  rate?: number;
  pitch?: number;
  preferredVoices?: string[];
  greeting?: string;
}

// The fact-find data schema now lives in the Resource Server (see the
// Fact-find Schema admin page, factFindSchemaApi). The intelligence-config
// surface below covers only persona / voice / provider / model.

export interface ChatUseCaseDefault {
  id: string;
  code: string;
  displayName: string;
  description?: string;
  defaultSystemPrompt: string;
  allowedRoles: string[];
  defaultToolWhitelist: string[];
  defaultLlmProvider: string;
  defaultModel: string;
  defaultTemperature: number;
  defaultMaxTokens: number;
  maxMessagesPerSession: number;
  enabled: boolean;
  agentName?: string;
  personality?: string;
  voiceEnabled?: boolean;
  voiceProfile?: VoiceProfile;
}

export interface ChatUseCaseOverride {
  id?: string;
  brokerageId: string;
  useCaseCode: string;
  systemPromptOverride?: string;
  toolWhitelistOverride?: string[];
  llmProviderOverride?: string;
  modelOverride?: string;
  monthlyTokenBudget?: number;
  rateLimitPerUserHour?: number;
  rateLimitPerBrokerageHour?: number;
  enabled?: boolean;
  agentNameOverride?: string;
  personalityOverride?: string;
  voiceEnabledOverride?: boolean;
  voiceProfileOverride?: VoiceProfile;
}

export type UpdateUseCaseDefaultRequest = Partial<Omit<ChatUseCaseDefault, 'id' | 'code'>>;

export interface UpdateUseCaseOverrideRequest extends Partial<Omit<ChatUseCaseOverride, 'id' | 'brokerageId' | 'useCaseCode'>> {
  clearFields?: string[];
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const response = await fetch(`${API_BASE}${path}`, {
    ...init,
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      ...(init?.headers ?? {}),
    },
  });
  if (!response.ok) {
    const text = await response.text().catch(() => '');
    throw new Error(`HTTP ${response.status}: ${text || response.statusText}`);
  }
  if (response.status === 204) return undefined as T;
  return response.json() as Promise<T>;
}

export const intelligenceAdminApi = {
  listUseCases: (): Promise<ChatUseCaseDefault[]> => request('/use-cases'),
  getUseCase: (code: string): Promise<ChatUseCaseDefault> => request(`/use-cases/${code}`),
  updateUseCase: (code: string, body: UpdateUseCaseDefaultRequest): Promise<ChatUseCaseDefault> =>
    request(`/use-cases/${code}`, { method: 'PUT', body: JSON.stringify(body) }),

  listProviders: (): Promise<ProviderDTO[]> => request('/providers'),
  listModels: (provider: string): Promise<ModelDTO[]> => request(`/providers/${provider}/models`),

  listOverrides: (code: string): Promise<ChatUseCaseOverride[]> =>
    request(`/use-cases/${code}/overrides`),
  getOverride: (code: string, brokerageId: string): Promise<ChatUseCaseOverride> =>
    request(`/use-cases/${code}/overrides/${brokerageId}`),
  updateOverride: (code: string, brokerageId: string, body: UpdateUseCaseOverrideRequest): Promise<ChatUseCaseOverride> =>
    request(`/use-cases/${code}/overrides/${brokerageId}`, { method: 'PUT', body: JSON.stringify(body) }),
  deleteOverride: (code: string, brokerageId: string): Promise<void> =>
    request(`/use-cases/${code}/overrides/${brokerageId}`, { method: 'DELETE' }),
};
